// src/pages/Collections.jsx
import { useEffect, useState } from "react";
import { Star, Filter, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const categories = ["All", "Men", "Women", "Kids"];
const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const colors = ["Black", "White", "Gray", "Blue", "Red", "Green", "Pink", "Yellow", "Navy"];

export default function Collections() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Fetch products
  useEffect(() => {
    fetch("/api/products/")
      .then((res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setProducts(list);
        setFiltered(list);
      })
      .catch((err) => {
        console.error(err);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Helpers from variants
  const getTotalStock = (p) => p.variants?.reduce((s, v) => s + v.stock, 0) || 0;
  const getAvailableColors = (p) => [...new Set(p.variants?.map(v => v.color).filter(Boolean))];
  const getAvailableSizes = (p) => [...new Set(p.variants?.map(v => v.size).filter(Boolean))];
  const getFirstImage = (p) => p.variants?.[0]?.image_url || "https://via.placeholder.com/300x400.png?text=No+Image";
  const getAvgRating = (p) => {
    const reviews = p.reviews || [];
    return reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;
  };

  // Apply filters
  useEffect(() => {
    let result = [...products];

    if (search) {
      result = result.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
    }

    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    if (selectedSizes.length) {
      result = result.filter(p => getAvailableSizes(p).some(s => selectedSizes.includes(s)));
    }
    if (selectedColors.length) {
      result = result.filter(p => getAvailableColors(p).some(c => selectedColors.includes(c)));
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "price-low": return a.price - b.price;
        case "price-high": return b.price - a.price;
        case "newest": return (b.id || 0) - (a.id || 0);
        case "rating": return getAvgRating(b) - getAvgRating(a);
        default: return 0;
      }
    });

    setFiltered(result);
  }, [search, priceRange, selectedSizes, selectedColors, sortBy, products]);

  const toggleFilter = (arr, setArr, value) => {
    setArr(prev => prev.includes(value) ? prev.filter(x => x !== value) : [...prev, value]);
  };

  const clearFilters = () => {
    setSearch("");
    setPriceRange([0, 5000]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSortBy("recommended");
  };

  // Fixed: FiltersSidebar component was missing!
  const FiltersSidebar = () => (
    <>
      <div className="mb-8">
        <h3 className="font-semibold text-gray-700 mb-4">Price Range</h3>
        <div className="text-lg font-bold mb-2">₹{priceRange[0]} - ₹{priceRange[1]}</div>
        <input
          type="range"
          min="0"
          max="5000"
          step="100"
          value={priceRange[1]}
          onChange={(e) => setPriceRange([priceRange[0], +e.target.value])}
          className="w-full h-3 bg-gray-200 rounded-lg cursor-pointer"
        />
      </div>

      <div className="mb-8">
        <h3 className="font-semibold text-gray-700 mb-4">Sizes</h3>
        <div className="grid grid-cols-3 gap-3">
          {sizes.map(size => (
            <button
              key={size}
              onClick={() => toggleFilter(selectedSizes, setSelectedSizes, size)}
              className={`py-3 rounded-lg font-bold border-2 transition-all ${
                selectedSizes.includes(size)
                  ? "bg-black text-white border-black"
                  : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-4">Colors</h3>
        <div className="grid grid-cols-3 gap-3">
          {colors.map(color => (
            <button
              key={color}
              onClick={() => toggleFilter(selectedColors, setSelectedColors, color)}
              className={`py-3 rounded-lg font-medium border-2 transition-all ${
                selectedColors.includes(color)
                  ? "bg-black text-white border-black"
                  : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"
              }`}
            >
              {color}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-black"></div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 px-4 md:px-8 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-10 text-gray-900">Our Collections</h1>

        {/* Search & Sort */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-6 py-4 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none text-lg"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-6 py-4 border-2 border-gray-200 rounded-xl text-lg"
          >
            <option value="recommended">Recommended</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="newest">Newest First</option>
            <option value="rating">Highest Rated</option>
          </select>
          <button
            onClick={() => setShowMobileFilters(true)}
            className="md:hidden flex items-center gap-3 px-6 py-4 bg-black text-white rounded-xl text-lg font-medium"
          >
            <Filter className="w-6 h-6" /> Filters
          </button>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-80 bg-white p-8 rounded-2xl shadow-lg border">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold">Filters</h2>
              <button onClick={clearFilters} className="text-sm underline text-red-600">Clear All</button>
            </div>
            <FiltersSidebar />
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            <p className="text-xl font-medium text-gray-700 mb-6">{filtered.length} products found</p>

            {filtered.length === 0 ? (
              <div className="text-center py-32">
                <p className="text-2xl text-gray-500 mb-6">No products match your filters</p>
                <button onClick={clearFilters} className="text-lg underline text-black font-medium">
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                {filtered.map((product) => {
                  const totalStock = getTotalStock(product);
                  const avgRating = getAvgRating(product);

                  return (
                    <div
                      key={product.id}
                      onClick={() => navigate(`/product/${product.id}`)}
                      className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group cursor-pointer"
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={getFirstImage(product)}
                          alt={product.name}
                          className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => e.target.src = "https://via.placeholder.com/300x400.png?text=No+Image"}
                        />
                        
                      </div>

                      <div className="p-5">
                        <h3 className="font-bold text-lg text-gray-800 truncate">{product.name}</h3>

                        <div className="mt-3 flex items-center gap-3">
                          <span className="text-2xl font-bold text-black">
                            ₹{product.offer_price || product.price}
                          </span>
                          {product.offer_price && (
                            <span className="text-lg text-gray-500 line-through">₹{product.price}</span>
                          )}
                        </div>

                        <p className={`mt-2 text-sm font-medium ${totalStock > 0 ? "text-green-600" : "text-red-600"}`}>
                          {totalStock > 0 ? `${totalStock} in stock` : "Out of Stock"}
                        </p>

                        <div className="flex items-center gap-1 mt-3">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-5 h-5 ${star <= avgRating ? "fill-yellow-500 text-yellow-500" : "text-gray-300"}`}
                            />
                          ))}
                          <span className="ml-2 text-sm text-gray-600">({avgRating})</span>
                        </div>

                        <button className="mt-5 w-full bg-black text-white py-4 rounded-xl font-bold hover:bg-gray-900 transition">
                          View Product
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filters */}
        {showMobileFilters && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
            <div className="bg-white w-full rounded-t-3xl p-8 max-h-screen overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold">Filters</h2>
                <button onClick={() => setShowMobileFilters(false)}>
                  <X className="w-8 h-8" />
                </button>
              </div>
              <FiltersSidebar />
              <button
                onClick={() => setShowMobileFilters(false)}
                className="mt-8 w-full py-4 bg-black text-white rounded-xl font-bold"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}